
import { z } from 'zod';

export const customerFormSchema = z.object({
  customer_type: z.enum(['residential', 'commercial']).default('residential'),
  // Residential fields
  name: z.string().optional(),
  last_name: z.string().optional(),
  cell_numbers: z.array(z.object({
    value: z.string().optional().refine(val => !val || val.length >= 6, 'Invalid number').refine(val => !val || /^[\d+\s()-]{6,}$/.test(val), 'Invalid number format')
  })).optional().default([]),
  emails: z.array(z.object({
    value: z.string().optional().refine(val => !val || z.string().email().safeParse(val).success, 'Invalid email format')
  })).optional().default([]),
  // Commercial fields
  business_name: z.string().optional(),
  vat_number: z.string().optional().refine(val => !val || /^[0-9]{10}$/.test(val.replace(/\s/g, '')), 'VAT number should be 10 digits'),
  business_phone: z.string().optional().refine(val => !val || /^[\d+\s()-]{6,}$/.test(val), 'Invalid phone format'),
  business_email: z.string().optional().refine(val => !val || z.string().email().safeParse(val).success, 'Invalid email format'),
  // Representative fields (commercial only, internal use)
  representative_first_name: z.string().optional(),
  representative_last_name: z.string().optional(),
  representative_position: z.string().optional(),
  representative_phone: z.string().optional().refine(val => !val || /^[\d+\s()-]{6,}$/.test(val), 'Invalid phone format'),
  representative_email: z.string().optional().refine(val => !val || z.string().email().safeParse(val).success, 'Invalid email format'),
  // Address fields (shared)
  street_address: z.string().optional(),
  suburb: z.string().optional(),
  city: z.string().optional(),
  province_state: z.string().optional(),
  postal_code: z.string().optional(),
  // Lead/tracking fields (shared)
  lead_source: z.string().optional(),
  lead_owner_id: z.string().nullable().optional(),
  lead_source_description: z.string().optional(),
  tags: z.array(z.string()).default([]),
  referred_by_id: z.string().nullable().optional(),
}).superRefine((data, ctx) => {
  // Conditional validation based on customer type
  if (data.customer_type === 'residential') {
    if (!data.name || data.name.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Name is required for residential customers',
        path: ['name'],
      });
    }
    if (!data.last_name || data.last_name.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Last name is required for residential customers',
        path: ['last_name'],
      });
    }
  } else if (data.customer_type === 'commercial') {
    if (!data.business_name || data.business_name.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Business name is required for commercial customers',
        path: ['business_name'],
      });
    }
  }
});

export type CustomerFormValidation = z.infer<typeof customerFormSchema>;

export const LEAD_SOURCES = [
  "Word of Mouth",
  "Website", 
  "LocalPros",
  "Bark",
  "Facebook",
  "Instagram",
  "LinkedIn",
  "Car Branding",
  "House Branding",
  "Youtube",
  "Other",
] as const;
